import { createClient } from "@supabase/supabase-js";
import { AUTH_STORAGE_KEY } from "./viewPrefs";
import type { Database } from "./database.types";

/* The one Supabase client, reaching the same project the phone reaches, the same way.
 *
 * ⚠️ THESE TWO VALUES ARE ALSO STATED IN `HybridTrainer/Services/Supabase/Supabase.swift`,
 * AND THAT IS THE ONE DELIBERATE DUPLICATION IN THIS APP. Two clients written in two languages
 * cannot share a constant, so instead the harness gate `b4-the-web-tokens-do-not-drift` reads
 * both files and fails the run if the project URL here stops matching the phone's. A web app
 * silently pointed at a different project is the kind of mistake that looks completely normal
 * until somebody's data is missing.
 *
 * The publishable key is safe in a client — that is what publishable means, and the phone has
 * shipped it for months. Row Level Security is the authority on who may read what, and it lives
 * in the database where BOTH surfaces meet it. Nothing here can widen it.
 *
 * There is deliberately no service-role key, no server and no second security surface. See
 * `docs/WEB-APP-PLAN.md` §1.
 */

export const SUPABASE_URL = "https://xtjbfaukkcfbdaadlldl.supabase.co";
export const SUPABASE_PUBLISHABLE_KEY = "sb_publishable_61Rk42S7rKNfTtg-_x4Bgg_pPoi1lhl";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    // The browser's own storage keeps the session, and the SDK refreshes it. Both are the
    // library defaults and are stated here because they are decisions, not accidents.
    persistSession: true,
    autoRefreshToken: true,
    // W140: the library's OWN default key (`sb-<project ref>-auth-token`), stated so `viewPrefs` can read the signed-in
    // coach out of the same store synchronously (the theme is stamped before the first paint) — one definition, two readers.
    storageKey: AUTH_STORAGE_KEY,
    detectSessionInUrl: true,
    // ⚠️ PKCE, AND ON THIS APP IT IS NOT A PREFERENCE — IT IS WHAT STOPS GOOGLE SIGN-IN
    // COLLIDING WITH THE ROUTER. MEASURED 2026-09-10: the installed `@supabase/auth-js`
    // (2.112.4) defaults to `flowType: 'implicit'` (`GoTrueClient.js` line 24), and the
    // implicit flow hands the session back in the URL's HASH FRAGMENT — which is precisely
    // where this app's router lives (`App.tsx` parses `window.location.hash` and listens for
    // `hashchange`). So the default would return `#access_token=...` and the router would read
    // it as a route, with `detectSessionInUrl` racing to strip it first.
    //
    // PKCE returns `?code=...` in the QUERY instead, which the hash router never looks at.
    //
    // ⚠️ IT CHANGES NOTHING FOR THE PASSWORD PATH. `signInWithPassword` uses no flow type at
    // all; PKCE governs OAuth, magic links and password recovery, and this app uses none of
    // those except the OAuth added beside it.
    flowType: "pkce",
  },
});
